import moment, { type Moment } from 'moment';

import type { BankFeedAccountType } from 'domain/bankFeed/schemas';
import type {
  DateRangeCustom,
  DateRangePreset,
  DateRangeValue,
  ToolbarFilters,
} from 'pages/company/BankFeedRoute/components/FilterToolbar/types';

export type DateRangeSelectValue = DateRangePreset | 'custom';

export const DATE_RANGE_PRESETS: DateRangePreset[] = ['last_30_days', 'last_90_days', 'last_12_months'];

export const isDateRangeCustom = (dr: DateRangeValue): dr is DateRangeCustom => typeof dr !== 'string';

export const dateRangeToSelectValue = (dr: DateRangeValue): DateRangeSelectValue =>
  isDateRangeCustom(dr) ? 'custom' : dr;

export const customRangeToMomentPair = (dr: DateRangeCustom): [Moment, Moment] => [
  moment(dr.from),
  moment(dr.to),
];

// FE → BE date-range mapping. BE resolves the window itself for `last_30d` /
// `last_90d` / `last_12m` — sending FE-side `date_from` / `date_to` for those
// would be redundant (and risk drift if BE definitions of "last 30 days"
// diverge). Only `custom` carries explicit bounds.
type NamedDatePeriod = 'last_30d' | 'last_90d' | 'last_12m';

const PRESET_TO_API_PERIOD: Record<DateRangePreset, NamedDatePeriod> = {
  last_30_days: 'last_30d',
  last_90_days: 'last_90d',
  last_12_months: 'last_12m',
};

type DateRangePayload =
  | { date_period: NamedDatePeriod }
  | { date_period: 'custom'; date_from: string; date_to: string };

// `Date#toISOString()` would format in UTC and silently shift the calendar day
// for users in positive-UTC offsets (e.g. Israel UTC+3: local midnight
// 2026-05-21 → `2026-05-20T21:00Z` → slice → `2026-05-20`). Picker gives
// Moment in local time; format via moment to preserve the user-picked
// calendar day regardless of timezone.
const toIsoDate = (d: Date): string => moment(d).format('YYYY-MM-DD');

export const buildDateRangePayload = (dr: DateRangeValue): DateRangePayload => {
  if (typeof dr === 'string') {
    return { date_period: PRESET_TO_API_PERIOD[dr] };
  }
  return {
    date_period: 'custom',
    date_from: toIsoDate(dr.from),
    date_to: toIsoDate(dr.to),
  };
};

// Single entry point for the date part of the grid-search / export payload, shared by
// useServerDatasource and ExportButton so the two never diverge. On the CARD tab (DA-19485) the
// selected payment date is sent verbatim as a one-day custom window (date_from === date_to ===
// the value-date option id, already an ISO 'YYYY-MM-DD' — no parsing/formatting round-trip). Every
// other case defers to the preset/custom range in `dateRange` (Bank tab). On CARD the grid stays
// mounted but withholds its datasource until a paymentDate seeds, so getRows only runs with a
// paymentDate set; the `dateRange` fallback here just guards the frame before the datasource attaches.
export const buildTransactionsDatePayload = (
  accountType: BankFeedAccountType,
  filters: Pick<ToolbarFilters, 'dateRange' | 'paymentDate'>,
): DateRangePayload =>
  accountType === 'CARD' && filters.paymentDate
    ? { date_period: 'custom', date_from: filters.paymentDate, date_to: filters.paymentDate }
    : buildDateRangePayload(filters.dateRange);
